import clsx from 'clsx'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import map from 'lodash/map'
import React, { CSSProperties, FC, ReactNode, Ref, useCallback, useContext, useRef, useState } from 'react'

import { ProductTileV2 } from '@Components/ProductList/ProductTiles/LoadMore/ProductTileV2/ProductTileV2'
import { SkeletonList } from '@Components/Skeleton/Skeleton'
import { PromptDialog } from '@Domains/Cart/Components/Prompt/Dialog'
import { useAddToCart } from '@Domains/Cart/Hooks/useAddToCart'
import { ProductCarousel } from '@Domains/Product/Features/ProductSlider/Components/ProductCarousel'
import { Product } from '@Generated/graphql'
import { parseAlgoliaAsset } from '@Globals/Algolia/Assets'
import { AlgoliaProductV2 } from '@Globals/Algolia/Types'
import { getChipsV2 } from '@Globals/CMS/ProductHelpers'
import { MetadataContext } from '@Globals/Metadata'

import styles from './ProductSlider.module.scss'

function isAlgoliaProduct(product?: Product | AlgoliaProductV2 | null): product is AlgoliaProductV2 {
  return !!product && !('masterVariant' in product)
}

export const getProductAsset = (product?: Product | AlgoliaProductV2): string | undefined => {
  if (!product) {
    return undefined
  }

  if (isAlgoliaProduct(product)) {
    // primaryImage only exists on already-mapped tiles (blockConfig
    // hitsArray), where algoliaProductToProductTile has expanded it to a
    // full URL — only the raw hit fields below carry the compact form
    if ('primaryImage' in product && isString(product.primaryImage)) {
      return product.primaryImage
    }

    return parseAlgoliaAsset(product.primaryAsset)?.url
      || parseAlgoliaAsset(product.assets?.[0])?.url
      || undefined
  }

  if ('primaryAsset' in product
    && isObject(product.primaryAsset)
    && 'url' in product.primaryAsset
    && !!product.primaryAsset.url
    && isString(product.primaryAsset.url)
  ) {
    return product.primaryAsset?.url || product.asset?.url || product.assets?.[0]?.url || undefined
  }

  return product.asset?.url || product.assets?.[0]?.url || undefined
}

export interface SliderProduct extends Product {
  sourceID?: string
}
interface ProductSliderStyleProps extends CSSProperties {
  thumbColor?: string
}
interface ProductSliderProps {
  className?: string
  'data-in-view'?: string
  header?: ReactNode
  hideArrows?: boolean
  hideBuyButtons?: boolean
  id?: string
  loading?: boolean
  productTileClassName?: string
  products: Array<AlgoliaProductV2 | SliderProduct>
  scrollToBottomOnPurchase?: boolean
  skipReset?: boolean
  sourceID?: string
  style?: ProductSliderStyleProps
  variantsOfID?: string
  withoutSuccessPrompt?: boolean
  wrapperRef?: Ref<any>
}

export const ProductSlider: FC<ProductSliderProps> = ({
  className,
  'data-in-view': dataInView,
  header,
  hideArrows,
  hideBuyButtons = false,
  id,
  loading,
  products = [],
  style,
  productTileClassName,
  scrollToBottomOnPurchase,
  skipReset,
  sourceID,
  variantsOfID,
  withoutSuccessPrompt,
  wrapperRef,
}) => {
  const { storeURL } = useContext(MetadataContext)
  const [
    addToCart, {
      isLoading,
      prompts,
      reset,
      variables,
    },
  ] = useAddToCart({
    sourceID,
  })
  const ref = useRef<HTMLDivElement | null>(null)
  const [currentAddToCardId, setCurrentAddToCardId] = useState<string | null>(null)
  const [currentSourceID, setCurrentSourceID] = useState<string | undefined>(undefined)

  const [addedIds, setAddedIds] = useState<string[]>([])

  const scrollToRef = useCallback(() => {
    if (scrollToBottomOnPurchase) {
      ref.current?.scrollIntoView({
        behavior: 'instant',
        block: 'end',
        inline: 'end',
      })
    }
  }, [scrollToBottomOnPurchase])

  const onBuy = useCallback((product) => {
    if (currentAddToCardId) {
      return
    }
    setCurrentAddToCardId(product.id)
    setCurrentSourceID(product.sourceID || sourceID)
    scrollToRef()
    addToCart(
      variantsOfID
        ? [
          {
            productID: variantsOfID,
            quantity: 1,
            variantID: product.id,
          },
        ]
        : [
          {
            productID: product.id,
            quantity: 1,
            variantID: '',
          },
        ],
      {
        sourceID: product.sourceID || sourceID,
        withoutSuccessPrompt,
        skipReset,
      },
    )
      .finally(() => {
        setAddedIds(prev => [...prev.filter(x => x !== product.id), product.id])
        setCurrentAddToCardId(null)
        scrollToRef()
      })
  }, [addToCart, currentAddToCardId, scrollToRef, skipReset, sourceID, variantsOfID, withoutSuccessPrompt])

  return (
    <>
      <ProductCarousel className={className} data-in-view={dataInView} header={header} hideArrows={hideArrows} id={id} style={style} wrapperRef={wrapperRef}>
        {loading
          ? (<SkeletonList className={clsx(productTileClassName, styles.productTile)} count={8} />)
          : map(products, (product) => {
            if (isAlgoliaProduct(product)) {
              return (
                <ProductTileV2
                  key={`product_slider_item_${product.id}`}
                  alreadyAddedToCart={withoutSuccessPrompt && addedIds.includes(product.id)}
                  bottomChips={product.bottomChips}
                  bundleCombinedValue={'bundleCombinedValue' in product && product.bundleCombinedValue}
                  className={`${productTileClassName} ${styles.productTile}`}
                  disableBuyButton={isLoading}
                  isOutOfStock={product.isOutOfStock}
                  loadingBuyButton={isLoading && currentAddToCardId === product.id}
                  onBuy={hideBuyButtons ? undefined : () => onBuy(product)}
                  originalPrice={product.beforePrice || product.price}
                  overwrittenPrice={product.beforePrice ? product.price.toString() : undefined}
                  primaryImage={getProductAsset(product)}
                  productID={product.id}
                  sourceID={product.sourceID || sourceID}
                  title={product.name || ('title' in product && product.title) || ''}
                  topChips={product.topChips}
                  url={storeURL(product.url || '')}
                />
              )
            }
            const { bottomChips, topChips } = getChipsV2(product)
            return (
              <ProductTileV2
                key={`product_slider_item_${product.id}`}
                alreadyAddedToCart={withoutSuccessPrompt && addedIds.includes(product.id)}
                bottomChips={bottomChips}
                className={`${productTileClassName} ${styles.productTile}`}
                disableBuyButton={isLoading}
                isOutOfStock={!!product.availability && !product.availability.isInStock}
                loadingBuyButton={isLoading && currentAddToCardId === product.id}
                onBuy={hideBuyButtons ? undefined : () => onBuy(product)}
                originalPrice={product.masterVariant?.price?.format || ''}
                overwrittenPrice={product.masterVariant?.price?.discount?.format || undefined}
                primaryImage={getProductAsset(product)}
                productID={product.id}
                sourceID={product.sourceID || sourceID}
                title={product.name || ''}
                topChips={topChips}
                url={product.url || ''}
              />
            )
          })}
      </ProductCarousel>
      {prompts && prompts.length !== 0 && (
        <PromptDialog
          isLoading={isLoading}
          onClose={reset}
          onSubmit={(addToCartData) => {
            addToCart(addToCartData, {
              sourceID: currentSourceID,
              withoutSuccessPrompt,
            })
              .then(() => {
                if (withoutSuccessPrompt) {
                  reset()
                }
                scrollToRef()
              })
          }}
          prompts={prompts}
          promptsOrigin={variables}
        />
      )}
      <div ref={ref} />
    </>
  )
}

